import { createSelector, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "~/store";
import { selectSurveys, setAsset } from "./assetSlice";
import type { BridgeModel } from "~/types/bridge";
import type { CloudImage } from "~/types/CloudImage";
import type { Annotation } from "~/types/annotations";

interface CompareState {
  enabled: boolean;
  surveyId: number | string | null;
  models: BridgeModel[];
  surveyFiles: {
    images: CloudImage[];
    general: Record<string, any>;
  } | null;
  folderTree: any[];
  imagesLocations: any[];
  folderStructure: any[];
  loading: boolean;
  error: string | null;
  heightOffset?: number;
  annotations: Annotation[];
  messages: any[];
}

const initialState: CompareState = {
  enabled: false,
  surveyId: null,
  models: [],
  surveyFiles: null,
  folderTree: [],
  imagesLocations: [],
  folderStructure: [],
  loading: false,
  error: null,
  heightOffset: 0,
  annotations: [],
  messages: [],
};

const compareSlice = createSlice({
  name: "compare",
  initialState,
  reducers: {
    setCompareEnabled: (state, action: PayloadAction<boolean>) => {
      state.enabled = action.payload;
    },
    setCompareSurveyId: (state, action: PayloadAction<string | number | null>) => {
      state.surveyId = action.payload;
    },
    setCompareModels: (state, action: PayloadAction<BridgeModel[]>) => {
      state.models = action.payload;
    },
    setCompareSurveyFiles: (state, action: PayloadAction<any>) => {
      state.surveyFiles = action.payload;
    },
    setCompareImagesLocations: (state, action: PayloadAction<any[]>) => {
      state.imagesLocations = action.payload;
    },
    setCompareFolderTree: (state, action: PayloadAction<any[]>) => {
      state.folderTree = action.payload;
    },
    setCompareFolderStructure: (state, action: PayloadAction<any[]>) => {
      state.folderStructure = action.payload;
    },
    setCompareHeightOffset: (state, action: PayloadAction<number>) => {
      state.heightOffset = action.payload;
    },
    setCompareAnnotations: (state, action: PayloadAction<Annotation[]>) => {
      state.annotations = action.payload;
    },
    setCompareMessages: (state, action: PayloadAction<any[]>) => {
      state.messages = action.payload;
    },
    resetCompareState: (state) => {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setAsset, (state) => {
      Object.assign(state, initialState);
    });
  },
});

export default compareSlice.reducer;

export const {
  setCompareSurveyId,
  setCompareModels,
  setCompareSurveyFiles,
  setCompareImagesLocations,
  setCompareFolderTree,
  setCompareFolderStructure,
  setCompareHeightOffset,
  setCompareAnnotations,
  setCompareMessages,
  setCompareEnabled,
  resetCompareState,
} = compareSlice.actions;

export const selectCompareEnabled = (state: RootState) => state.compare.enabled;
export const selectCompareSurveyId = (state: RootState) => state.compare.surveyId;
export const selectCompareModels = (state: RootState) => state.compare.models;
export const selectCompareSurveyFiles = (state: RootState) => state.compare.surveyFiles;
export const selectCompareImagesLocations = (state: RootState) => state.compare.imagesLocations;
export const selectCompareImagesFolderTree = (state: RootState) => state.compare.folderTree;
export const selectCompareHeightOffset = (state: RootState) => state.compare.heightOffset;
export const selectCompareAnnotations = (state: RootState) => state.compare.annotations;
export const selectCompareMessages = (state: RootState) => state.compare.messages;

export const selectCompareSurvey = createSelector(
  selectCompareSurveyId,
  selectSurveys,
  (surveyId, surveys) => {
    if (!surveyId || !surveys) return null;
    const survey = surveys.find((item: any) => item.id == surveyId);
    return survey || null;
  },
);
